import { Alert, Button, Drawer, Flex, Form, Notification, Page, Spin } from "@payping/web-components";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { DashboardRoutes } from "@/router/constant";
import { Close } from "@payping/icons";
import { SubLayout } from "@/components/SubLayout";

import AddNewIBANModal from "./AddNewIBANModal";
import { ApprovedIBANsSection, EmptyBankState, PendingIBANsSection } from "./components";
import { useIBANManagement } from "./hooks/useIBANManagement";

enum IBANStatus {
  REQUESTED = "requested",
  IN_PROGRESS = "in_progress",
  SETTLEMENT_READY = "settlement_ready",
  ACTIVE_SETTLEMENT = "active_settlement",
  REJECTED = "rejected",
  APPROVED = "approved",
}

const BankList: React.FC = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm<{ title: string; iban: string }>();
  const [selectedIBANId, setSelectedIBANId] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [notification, contextHolder] = Notification.useNotification({
    top: 16,
    duration: 5,
    maxCount: 1,
    placement: "top",
  });

  const showNoActiveIbanAlert = searchParams.get("noActiveIban") === "true";

  const {
    approvedIBANList,
    pendingIBANList,
    rejectedIBANList,
    activeIBAN,
    showAddIBANModal,
    setShowAddIBANModal,
    handleAddIBAN,
    handleEditIBAN,
    handleRemoveIBAN,
    handleModalClose,
    retryLastRejectedRequest,
    retryRequestLoading,
    loading,
    getActiveIBAN,
    updateActiveIBAN,
    contextHolder: ibanContextHolder,
  } = useIBANManagement();

  const getStatusInfo = (status: IBANStatus) => {
    switch (status) {
      case IBANStatus.REQUESTED:
        return { color: "warning", text: t("profile.bank.status.requested"), icon: null };
      case IBANStatus.IN_PROGRESS:
        return { color: "warning", text: t("profile.bank.status.inProgress"), icon: null };
      case IBANStatus.SETTLEMENT_READY:
        return { color: "success", text: t("profile.bank.status.settlementReady"), icon: null };
      case IBANStatus.ACTIVE_SETTLEMENT:
        return { color: "success", text: t("profile.bank.status.activeSettlement"), icon: null };
      case IBANStatus.REJECTED:
        return { color: "error", text: t("profile.bank.status.rejected"), icon: null };
      case IBANStatus.APPROVED:
        return { color: "success", text: t("profile.bank.active"), icon: null };
      default:
        return { color: "default", text: t("profile.bank.status.unknown"), icon: null };
    }
  };

  const loadActiveIBAN = useCallback(async () => {
    try {
      const response = await getActiveIBAN();
      if (response?.iban) {
        const matchingIBAN = approvedIBANList.find((iban) => iban.iban === response.iban);
        if (matchingIBAN) {
          setSelectedIBANId(matchingIBAN.id);
        }
      }
    } catch (error) {
      /* empty */
    }
  }, [approvedIBANList, getActiveIBAN]);

  useEffect(() => {
    // Load active IBAN on mount
    getActiveIBAN();
  }, [getActiveIBAN]);

  useEffect(() => {
    if (approvedIBANList.length > 0) {
      loadActiveIBAN();
    }
  }, [approvedIBANList, loadActiveIBAN]);

  const handleIBANSelection = (ibanId: string) => {
    setSelectedIBANId(ibanId);
  };

  const handleSaveAndConfirm = async () => {
    if (!selectedIBANId) {
      notification.error({
        message: t("profile.bank.pleaseSelectIBAN"),
        closable: true,
      });
      return;
    }

    const selectedIBAN = approvedIBANList.find((iban) => iban.id === selectedIBANId);
    if (!selectedIBAN) {
      notification.error({
        message: t("profile.bank.invalidSelection"),
        closable: true,
      });
      return;
    }

    setIsUpdating(true);
    try {
      await updateActiveIBAN(selectedIBAN.iban);
      await loadActiveIBAN();
      notification.success({
        message: t("profile.bank.updateSuccess"),
        closable: true,
      });
      navigate(DashboardRoutes.settings.bank.root.url);
    } catch (error) {
      notification.error({
        message: t("profile.bank.updateError"),
        closable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      {contextHolder}
      {ibanContextHolder}
      <Drawer
        title={t("profile.bank.editBankInfo")}
        open={true}
        height="100%"
        placement="bottom"
        closeIcon={false}
        extra={
          <Button
            type="text"
            icon={<Close width={14} height={14} />}
            onClick={() => navigate(DashboardRoutes.settings.bank.root.url)}
          />
        }
      >
        <Page>
          <SubLayout>
            <Flex vertical gap="24px" className="tw-w-full md:tw-mx-auto md:tw-w-1/2">
              {showNoActiveIbanAlert && (
                <Alert
                  message={t("profile.bank.noActiveIbanMessage")}
                  type="warning"
                  showIcon
                  className="tw-mb-4"
                  closable
                  onClose={() => {
                    searchParams.delete("noActiveIban");
                    setSearchParams(searchParams);
                  }}
                />
              )}
              {(pendingIBANList.length > 0 || rejectedIBANList.length > 0) && (
                <Alert
                  message={t("profile.bank.pendingRequestWarningWithShaparak")}
                  type="warning"
                  className="tw-mb-4"
                />
              )}
              {approvedIBANList.length === 0 &&
                !loading &&
                pendingIBANList.length === 0 &&
                rejectedIBANList.length === 0 && <EmptyBankState onAddNew={() => setShowAddIBANModal(true)} />}

              {loading ? (
                <Flex justify="center" align="center" className="tw-py-8">
                  <Spin size="large" />
                </Flex>
              ) : (
                <>
                  <ApprovedIBANsSection
                    approvedIBANs={approvedIBANList}
                    onSettlementSelection={handleIBANSelection}
                    onAddNewIBAN={() => setShowAddIBANModal(true)}
                    onRemoveIBAN={handleRemoveIBAN}
                    getStatusInfo={getStatusInfo}
                    selectedIBANId={selectedIBANId}
                    hasPendingRequests={pendingIBANList.length > 0}
                    activeIBAN={activeIBAN}
                    hasPendingIBANs={pendingIBANList.length > 0 || rejectedIBANList.length > 0}
                  />

                  <PendingIBANsSection
                    pendingIBANs={[...pendingIBANList, ...rejectedIBANList]}
                    onEditIBAN={handleEditIBAN}
                    onRemoveIBAN={handleRemoveIBAN}
                    onRetryRejected={retryLastRejectedRequest}
                    retryRequestLoading={retryRequestLoading}
                    getStatusInfo={getStatusInfo}
                    activeIBAN={activeIBAN}
                  />
                </>
              )}

              {approvedIBANList.length > 0 && (
                <div className="tw-border-gray-200 tw-mt-8 tw-border-t tw-pt-6">
                  <Flex gap="12px" className="tw-justify-start">
                    <Button
                      size="large"
                      onClick={() => navigate(DashboardRoutes.settings.bank.root.url)}
                      className="tw-w-full tw-min-w-24 md:tw-w-max"
                    >
                      {t("profile.bank.back")}
                    </Button>
                    <Button
                      type="primary"
                      size="large"
                      className="tw-w-full tw-min-w-24 md:tw-w-max"
                      onClick={handleSaveAndConfirm}
                      loading={isUpdating}
                    >
                      {t("profile.bank.saveAndConfirm")}
                    </Button>
                  </Flex>
                </div>
              )}

              {(pendingIBANList.length > 0 || rejectedIBANList.length > 0) && approvedIBANList.length === 0 && (
                <div className="tw-border-gray-200 tw-mt-8 tw-border-t tw-pt-6">
                  <Button
                    size="large"
                    onClick={() => navigate(DashboardRoutes.settings.bank.root.url)}
                    className="tw-w-full tw-min-w-24 md:tw-w-max"
                  >
                    {t("profile.bank.back")}
                  </Button>
                </div>
              )}
            </Flex>
          </SubLayout>
        </Page>

        <AddNewIBANModal
          loading={loading}
          open={showAddIBANModal}
          onCancel={handleModalClose}
          onFinish={handleAddIBAN}
          form={form}
        />
      </Drawer>
    </>
  );
};

export default BankList;
